import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useBillingStore } from '../../store/billingStore';
import { X, Loader2, CreditCard, Lock } from 'lucide-react';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY || '');

interface AddCardModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

/* ── Inner form (requires Stripe Elements context with clientSecret) ── */
const SetupForm: React.FC<AddCardModalProps> = ({ onClose, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsSubmitting(true);
    setError(null);

    const { error: stripeError } = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    });

    if (stripeError) {
      setError(stripeError.message || 'Erreur lors de l\'enregistrement');
      setIsSubmitting(false);
    } else {
      onSuccess();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <label className="block text-xs font-semibold text-text-secondary mb-2">
          Informations de la carte
        </label>
        <PaymentElement onReady={() => setReady(true)} options={{ layout: 'tabs' }} />
      </div>

      {error && (
        <p className="text-sm text-error bg-error/10 px-3 py-2 rounded-lg">{error}</p>
      )}

      <div className="flex items-center gap-2 text-[11px] text-text-muted">
        <Lock className="h-3 w-3" />
        Sécurisé par Stripe. Vos données ne transitent jamais par nos serveurs.
      </div>

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onClose}
          className="flex-1 px-4 py-2.5 rounded-xl border border-border-color text-sm font-medium text-text-secondary hover:bg-highlight/50 transition-colors"
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={!stripe || isSubmitting || !ready}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-accent-color text-black text-sm font-bold hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <CreditCard className="h-4 w-4" />
          )}
          {isSubmitting ? 'Enregistrement…' : 'Enregistrer la carte'}
        </button>
      </div>
    </form>
  );
};

/* ── Outer modal — fetches SetupIntent first, then mounts Elements with clientSecret ── */
const AddCardModal: React.FC<AddCardModalProps> = ({ onClose, onSuccess }) => {
  const { createSetupIntent } = useBillingStore();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const secret = await createSetupIntent();
      if (secret) setClientSecret(secret);
      else setError('Impossible de préparer le formulaire. Réessayez.');
    })();
  }, []);

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-in"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative bg-bg-primary rounded-2xl w-full max-w-md p-6 border border-border-color shadow-2xl shadow-black/50 animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-accent-color" />
            <h2 className="text-base font-bold text-text-primary">Nouvelle carte</h2>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-lg bg-bg-tertiary/80 border border-border-color flex items-center justify-center text-text-muted hover:text-text-primary transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {error && (
          <p className="text-sm text-error bg-error/10 px-3 py-2 rounded-lg">{error}</p>
        )}

        {!clientSecret && !error && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-text-muted" />
          </div>
        )}

        {clientSecret && (
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret,
              appearance: {
                theme: 'night',
                variables: { colorPrimary: '#00ccff' },
              },
            }}
          >
            <SetupForm onClose={onClose} onSuccess={onSuccess} />
          </Elements>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default AddCardModal;
